import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackButton from "../components/BackButton";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchActowizProducts } from "../store/actowiz/actowizSlice";
import { apiRequest } from "../api/client";
import { useToast } from "../components/ToastProvider";

const PLATFORM_LABELS = {
  tesco: "Tesco",
  asda: "ASDA",
  morrison: "Morrisons",
  sainsbury: "Sainsbury's",
};

const parseMoney = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).trim().replaceAll(",", ".").replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseRatingValue = (value) => {
  if (value == null) return null;
  const parsed = Number.parseFloat(String(value).trim().replace(",", ".").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseReviewCountValue = (value) => {
  if (value == null) return null;
  const parsed = Number.parseInt(String(value).replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const getProductImages = (prod) => {
  if (!prod) return [];
  if (Array.isArray(prod.images) && prod.images.length) {
    return prod.images.filter(Boolean);
  }
  const fallback = [
    prod.image,
    prod.image_url,
    prod.imageUrl,
    prod.thumbnail,
    prod.thumb,
  ].filter(Boolean);
  return fallback;
};

function ProductDetailSingle({ onOpenAuth, onOpenMenu }) {
  const { platform, id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const statusByPlatform = useAppSelector((s) => s.actowiz.statusByPlatform);
  const errorByPlatform = useAppSelector((s) => s.actowiz.errorByPlatform);

  const p = String(platform || "").trim().toLowerCase();
  const platformLabel = PLATFORM_LABELS[p] || "Retailer";

  const [didKickoffFetch, setDidKickoffFetch] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const { showToast } = useToast();

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  const matchesId = (prod, matchId) => {
    if (!prod || matchId == null) return false;
    const candidateIds = [
      prod?.pid,
      prod?.product_id,
      prod?.sku,
      prod?.id,
      prod?.productId,
      prod?.productID,
    ]
      .map((v) => (v == null ? "" : String(v)))
      .filter(Boolean);
    return candidateIds.includes(String(matchId));
  };

  useEffect(() => {
    if (!p || !id) return;
    const list = byPlatform?.[p] || [];
    const alreadyHas = Array.isArray(list) && list.some((x) => matchesId(x, id));
    if (alreadyHas) return;

    // Fetch from products API (no product_detail call).
    setDidKickoffFetch(true);
    dispatch(
      fetchActowizProducts({
        platform: p,
        start: 0,
        offset: 60,
        mode: "replace",
        force: true,
      })
    );
  }, [dispatch, p, id, byPlatform]);

  const product = useMemo(() => {
    const list = byPlatform?.[p] || [];
    if (!Array.isArray(list) || !id) return null;
    return list.find((x) => matchesId(x, id)) || null;
  }, [id, p, byPlatform]);

  const images = useMemo(() => getProductImages(product), [product]);
  const mainImage = images[0];

  const numericPrice = useMemo(
    () =>
      parseMoney(
        product?.price ??
          product?.unit_price ??
          product?.price_per_unit ??
          product?.pricePerUnit ??
          product?.offer_price ??
          product?.offerPrice
      ),
    [product]
  );

  const priceLabel = useMemo(() => {
    if (numericPrice != null) return `£${numericPrice.toFixed(2)}`;
    return "£—";
  }, [numericPrice]);
  const ratingValue = useMemo(
    () => parseRatingValue(product?.average_rating ?? product?.avg_rating ?? product?.rating),
    [product]
  );
  const reviewCountValue = useMemo(
    () =>
      parseReviewCountValue(
        product?.review_count ?? product?.reviews ?? product?.reviewCount ?? product?.rating_count
      ),
    [product]
  );

  const sizeOptions = useMemo(() => {
    const raw = String(product?.packsize || "").trim();
    const list = raw
      ? raw
          .split(/[,/|]/g)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    return list.length ? list.slice(0, 6) : ["—"];
  }, [product?.packsize]);

  const [selectedSize, setSelectedSize] = useState("");
  useEffect(() => {
    if (!sizeOptions.length) return;
    setSelectedSize((prev) => (prev && sizeOptions.includes(prev) ? prev : sizeOptions[0] || ""));
  }, [id, sizeOptions]);

  const status = statusByPlatform?.[p] || "idle";
  const error = errorByPlatform?.[p] || "";

  async function handleAddToList() {
    try {
      setAddBusy(true);
      const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
      if (!minimalRes.ok) {
        const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
        throw new Error(errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists.");
      }

      const lists = Array.isArray(minimalRes.data)
        ? minimalRes.data
        : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];

      if (!lists.length) {
        showToast({
          type: "error",
          title: "No grocery list",
          message: "No grocery list found. Please create a list first.",
          actionLabel: "Create list",
          onAction: () => navigate("/settings?tab=grocery&view=create"),
        });
        return;
      }

      let selectedListId = null;
      if (lists.length === 1) {
        selectedListId = lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
      } else {
        const selected = await chooseList(lists);
        if (!selected) return;
        selectedListId = selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
      }
      if (!selectedListId) throw new Error("Unable to select grocery list.");

      const productId = product?.product_id ?? product?.pid ?? product?.sku ?? product?.id ?? id ?? null;
      if (!productId) throw new Error("Invalid product.");

      const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
        method: "POST",
        body: {
          product_id: String(productId),
          image_url: images[0] || "",
          price: numericPrice ?? "",
          weight: selectedSize || product?.packsize || product?.weight || "",
          quantity: 1,
        },
      });
      if (!addRes.ok) {
        const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
        throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
      }

      showToast({
        type: "success",
        title: "Added to list",
        message: "Product added to grocery list.",
        actionLabel: "Go to Grocery List",
        onAction: () => navigate("/settings?tab=grocery&view=lists"),
      });
    } catch (err) {
      showToast({
        type: "error",
        title: "Add failed",
        message: err?.message || "Failed to add product to list.",
      });
    } finally {
      setAddBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 md:px-6">
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <BackButton />
            <button
              type="button"
              onClick={() => navigate(`/platform/${encodeURIComponent(p)}`)}
              className="rounded-full bg-[#7352C7] px-4 py-2 text-xs font-extrabold text-white shadow-sm hover:bg-[#6546c3]"
            >
              View all {platformLabel}
            </button>
          </div>
        </div>

        {status === "loading" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-center text-sm text-[#6E7B86] shadow-sm">
            Loading product details…
          </div>
        )}

        {status === "failed" && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-center text-sm text-red-700 shadow-sm">
            {error || "Failed to load product details."}
          </div>
        )}

        {status !== "loading" && !product && !error && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-center text-sm text-[#6E7B86] shadow-sm">
            {didKickoffFetch ? "Product not found." : "Loading…"}
          </div>
        )}

        {product && (
          <div className="grid gap-8 md:grid-cols-[280px_minmax(0,1fr)]">
            <div className="space-y-4">
              <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                {mainImage ? (
                  <img
                    src={mainImage}
                    alt={product?.title || product?.sku}
                    className="max-h-64 object-contain"
                  />
                ) : (
                  <div className="flex h-64 w-full items-center justify-center rounded-xl bg-[#F5F7FB] text-xs text-[#6E7B86]">
                    No Image
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#7352C7]/10 px-3 py-1 text-[11px] font-extrabold text-[#7352C7]">
                    {platformLabel}
                  </div>
                  <h1 className="mt-3 text-2xl font-extrabold text-[#24303A]">
                    {product?.title || product?.sku || "N/A"}
                  </h1>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="rounded-full bg-slate-200 px-3 py-1 font-semibold text-[#24303A]">
                      {product?.packsize || ""}
                    </span>
                  </div>
                </div>

                <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-right text-xs border border-emerald-100">
                  <p className="text-[11px] font-semibold text-emerald-700">Price</p>
                  <p className="mt-1 text-2xl font-extrabold text-emerald-800">{priceLabel}</p>
                  {(Number.isFinite(ratingValue) || Number.isFinite(reviewCountValue)) && (
                    <div className="mt-1 inline-flex items-center justify-center gap-1 leading-none text-[11px] font-semibold text-emerald-700">
                      {Number.isFinite(ratingValue) && (
                        <>
                          <StarRating rating={ratingValue} />
                          <span className="leading-none">{ratingValue.toFixed(1)}</span>
                        </>
                      )}
                      {Number.isFinite(reviewCountValue) && (
                        <span className="leading-none">({reviewCountValue} reviews)</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <section className="space-y-2 text-sm">
                <h2 className="text-sm font-extrabold text-[#24303A]">Good to know</h2>
                <p className="text-xs text-[#6E7B86] whitespace-pre-line">
                  {product?.description
                    ? product.description
                    : "Product description is not available for this item yet."}
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-sm font-extrabold text-[#24303A]">Available sizes</h2>
                <div className="space-y-2">
                  {sizeOptions.map((size) => {
                    const isActive = size === selectedSize;
                    return (
                      <button
                        key={size}
                        type="button"
                        onClick={() => setSelectedSize(size)}
                        className={[
                          "w-full rounded-2xl border p-3 text-left transition",
                          isActive
                            ? "border-[#7352C7] bg-[#7352C7]/5"
                            : "border-slate-200 bg-slate-50 hover:bg-white",
                        ].join(" ")}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
                              {mainImage ? (
                                <img
                                  src={mainImage}
                                  alt={product?.title || product?.sku}
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <div className="text-[10px] font-semibold text-slate-400">No image</div>
                              )}
                            </div>
                            <span
                              className={[
                                "rounded-full px-3 py-1 text-[11px] font-semibold",
                                isActive
                                  ? "bg-[#7352C7] text-white"
                                  : "border border-slate-200 bg-white text-slate-700",
                              ].join(" ")}
                            >
                              {size}
                            </span>
                          </div>
                          <span className="text-xs font-extrabold text-slate-900">{priceLabel}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              {product?.url ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-2xl border-2 border-slate-700 bg-white px-4 py-3 text-sm font-extrabold text-slate-900"
                    onClick={handleAddToList}
                    disabled={addBusy}
                  >
                    {addBusy ? "Adding..." : "Add to List"}
                  </button>
                  <a
                    href={product.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-2xl bg-[#7352C7] px-4 py-3 text-sm font-extrabold text-white hover:bg-[#6546c3]"
                  >
                    Visit Store
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </main>

      <Footer />

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-xs text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-sm font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetailSingle;

function StarRating({ rating }) {
  const clamped = Math.max(0, Math.min(5, rating || 0));
  return (
    <div
      className="inline-flex items-center gap-0.5 leading-none"
      aria-label={`Rating ${clamped.toFixed(1)} out of 5`}
    >
      {[0, 1, 2, 3, 4].map((index) => {
        const fillPercent = Math.max(0, Math.min(1, clamped - index));
        return (
          <span key={index} className="relative inline-flex h-3.5 w-3.5 items-center justify-center leading-none">
            <span className="absolute inset-0 flex items-center justify-center leading-none text-slate-300">
              ★
            </span>
            <span
              className="absolute inset-0 flex items-center justify-start overflow-hidden leading-none text-amber-400"
              style={{ width: `${fillPercent * 100}%` }}
            >
              ★
            </span>
          </span>
        );
      })}
    </div>
  );
}

